import { LitElement, html, css, classMap } from '@hh/global.js'

class HHStickyAtc extends LitElement {
  static properties = {
    _show: {
      type: Boolean,
      state: true,
    },
    _intersectFooter: {
      type: Boolean,
      state: true,
    },
    _overFullbleed: {
      type: Boolean,
      state: true,
    },
    isStatic: {
      type: Boolean,
      attribute: 'is-static'
    },
  }

  constructor() {
    super()
    this._overFullbleed = false
    this.onResizeHandler = this.observeFullbleed.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.mainProductForm =
      this.closest('.js-main-product')?.querySelector('hh-product-form')

    this.syncVisibility = this.syncVisibility.bind(this)
    this.syncVisibility()
    this.observeBuyButtons()

    if (this.mainProductForm) {
      this.mainProductForm.addEventListener(
        'productAdded',
        this.onSubmitSuccess.bind(this)
      )
    }

    if (this.mainProductForm && this.atcButton) {
      this.atcButton.addEventListener('click', this.onAddToCartClick.bind(this))
    }

    if (!this.isStatic) {
      const productCtaWrapper = document.querySelector('.js-product-container')
      this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
        threshold: 0,
        rootMargin: '-400px 0px 0px 0px'
      })

      this.observer.observe(productCtaWrapper)
    }

    const footer = document.querySelector('footer')
    this.footerObserver = new IntersectionObserver(this.onFooterIntersection.bind(this), {
      threshold: 0,
      rootMargin: "100px 0px 0px 0px"
    })

    this.footerObserver.observe(footer)
    this.observeFullbleed()
    window.addEventListener('resize', this.onResizeHandler)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.mainProductForm?.removeEventListener(
      'productAdded',
      this.onSubmitSuccess
    )
    this.observer?.disconnect()
    this.footerObserver?.disconnect()
    this.fullbleedObserver?.disconnect()
    this.buyButtonsObserver?.disconnect()
    window.removeEventListener('resize', this.onResizeHandler)
  }

  isBuyButtonsVisible() {
    const root = this.closest('.js-main-product')
    const buyButtons = root?.querySelector('[data-buy-buttons]')
    if (!buyButtons) return false

    if (typeof buyButtons.checkVisibility === 'function') {
      return buyButtons.checkVisibility()
    }

    let el = buyButtons
    while (el && el !== root) {
      const style = window.getComputedStyle(el)
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false
      }
      el = el.parentElement
    }

    return true
  }

  syncVisibility() {
    this.classList.toggle('hidden', !this.isBuyButtonsVisible())
  }

  observeBuyButtons() {
    const root = this.closest('.js-main-product')
    if (!root) {
      this.classList.add('hidden')
      return
    }

    this.buyButtonsObserver?.disconnect()
    this.buyButtonsObserver = new MutationObserver(this.syncVisibility)

    const buyButtons = root.querySelector('[data-buy-buttons]')
    if (buyButtons) {
      this.buyButtonsObserver.observe(buyButtons, {
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden'],
      })

      const editorBlock = buyButtons.closest('[data-shopify-editor-block]')
      if (editorBlock && editorBlock !== buyButtons) {
        this.buyButtonsObserver.observe(editorBlock, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        })
      }
      return
    }

    this.buyButtonsObserver.observe(root, { childList: true, subtree: true })
  }

  onAddToCartClick(e) {
    e.preventDefault()
    if (!this.mainProductForm) return

    const { target } = e
    this.mainProductForm.dispatchEvent(
      new CustomEvent('addToCart', { bubbles: true })
    )

    target.setAttribute('disabled', true)
    target.classList.add('loading')
  }

  onSubmitSuccess() {
    if (this.atcButton?.hasAttribute('disabled')) {
      this.atcButton.removeAttribute('disabled')
      this.atcButton.classList.remove('loading')
    }
  }

  onIntersection([entry]) {
    const { isIntersecting } = entry
    this._show = !isIntersecting
  }

  onFooterIntersection([entry]) {
    const { isIntersecting } = entry
    this._intersectFooter = isIntersecting
  }

  getVisibleFullbleeds() {
    return [...document.querySelectorAll('[data-fullbleed]')].filter((el) => {
      const style = window.getComputedStyle(el)
      return style.display !== 'none' && style.visibility !== 'hidden' && el.getClientRects().length > 0
    })
  }

  observeFullbleed() {
    const fullbleeds = this.getVisibleFullbleeds()
    // Last stacked fullbleed — stay hidden until the whole group is past
    const fullbleed = fullbleeds[fullbleeds.length - 1]

    this.fullbleedObserver?.disconnect()

    if (!fullbleed) {
      this._overFullbleed = false
      return
    }

    this._overFullbleed = true
    this.fullbleedObserver = new IntersectionObserver(([entry]) => {
      const pastFullbleed = !entry.isIntersecting && entry.boundingClientRect.top < 0
      this._overFullbleed = !pastFullbleed
    })

    this.fullbleedObserver.observe(fullbleed)
  }

  get atcButton() {
    return this.querySelector('.js-sticky-atc') ?? null
  }

  render() {
    return html`
      <section
        class=${classMap({
      container: true,
      show: !this._intersectFooter && !this._overFullbleed && (this.isStatic || this._show),
      motion: true,
    })}
      >
        <slot></slot>
      </section>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .container {
      padding: var(--hh-space-xs);
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      z-index: 2;
      transform: translate3d(0, 100%, 0);
      transition: transform var(--hh-duration-long) ease;
      will-change: transform;
    }

    @media (min-width: 750px) {
      .container {
        width: max(660px, 45vw);
        left: auto;
        right: var(--hh-space-xs);
        bottom: var(--hh-space-sm);
        transform: translate3d(calc(100% + 2rem), 0, 0);
      }
    }

    .container.show {
      transform: translate3d(0, 0, 0);
    }

    .hide {
      display: none;
    }

    @media (prefers-reduced-motion) {
      .motion,
      .motion * {
        animation: none !important;
        transition: none !important;
      }
    }
  `
}

customElements.define('hh-sticky-atc', HHStickyAtc)
